import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { BarChart3, Plus } from 'lucide-react';

const DailyReportsManagement = () => {
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    income: '',
    expenses: '',
    cashbox_balance: '',
    notes: ''
  });

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/daily-reports`);
      // Sort reports by date - newest first
      const sortedReports = response.data.sort((a, b) => {
        return new Date(b.date || b.created_at) - new Date(a.date || a.created_at);
      });
      setReports(sortedReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const reportData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        income: parseFloat(formData.income),
        expenses: parseFloat(formData.expenses),
        cashbox_balance: parseFloat(formData.cashbox_balance)
      };
      
      await axios.post(`${API}/daily-reports`, reportData);
      setFormData({ date: '', income: '', expenses: '', cashbox_balance: '', notes: '' });
      setIsDialogOpen(false);
      fetchReports();
    } catch (error) {
      console.error('Error saving report:', error);
      alert(error.response?.data?.detail || 'حدث خطأ أثناء حفظ التقرير');
    }
  };

  const handleApprove = async (reportId) => {
    try {
      await axios.put(`${API}/daily-reports/${reportId}/approve`);
      fetchReports();
    } catch (error) {
      console.error('Error approving report:', error);
      alert('حدث خطأ أثناء الموافقة على التقرير');
    }
  };

  const handleReject = async (reportId) => {
    const reason = prompt('أدخل سبب الرفض (اختياري):');
    try {
      await axios.put(`${API}/daily-reports/${reportId}/reject`, null, {
        params: { rejection_reason: reason || '' }
      });
      fetchReports();
    } catch (error) {
      console.error('Error rejecting report:', error);
      alert('حدث خطأ أثناء رفض التقرير');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { variant: 'secondary', text: '⏳ في انتظار الموافقة', color: 'bg-yellow-100 text-yellow-800' },
      'approved': { variant: 'default', text: '✅ تمت الموافقة', color: 'bg-green-100 text-green-800' },
      'rejected': { variant: 'destructive', text: '❌ مرفوض', color: 'bg-red-100 text-red-800' }
    };
    const statusInfo = statusMap[status] || statusMap.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>{statusInfo.text}</span>;
  };

  const canCreateReports = user?.role === 'agency_staff' || user?.role === 'super_admin';
  const canApproveReports = user?.role === 'general_accountant' || user?.role === 'super_admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">📈 {t('dailyReports')}</h2>
            <p className="text-gray-600 mt-1">إدارة ومراجعة التقارير اليومية</p>
          </div>
          {canCreateReports && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-600 hover:bg-brand-700">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('createReport')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>➕ إنشاء تقرير يومي جديد</DialogTitle>
                  <DialogDescription>
                    أدخل بيانات التقرير اليومي
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="date">📅 التاريخ</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="income">💰 الإيرادات (دج)</Label>
                    <Input
                      id="income"
                      type="number"
                      step="0.01"
                      value={formData.income}
                      onChange={(e) => setFormData({ ...formData, income: e.target.value })}
                      placeholder="0.00"
                      required
                      className="text-right"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="expenses">📉 المصروفات (دج)</Label>
                    <Input
                      id="expenses"
                      type="number"
                      step="0.01"
                      value={formData.expenses}
                      onChange={(e) => setFormData({ ...formData, expenses: e.target.value })}
                      placeholder="0.00"
                      required
                      className="text-right"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="cashbox_balance">🏦 رصيد الصندوق (دج)</Label>
                    <Input
                      id="cashbox_balance"
                      type="number"
                      step="0.01"
                      value={formData.cashbox_balance}
                      onChange={(e) => setFormData({ ...formData, cashbox_balance: e.target.value })}
                      placeholder="0.00"
                      required
                      className="text-right"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="notes">📝 ملاحظات</Label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="ملاحظات إضافية (اختياري)"
                      className="w-full p-2 border border-gray-300 rounded-md text-right"
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="bg-brand-600 hover:bg-brand-700">
                      📊 إنشاء التقرير
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BarChart3 className="h-5 w-5 ml-2" />
            قائمة التقارير اليومية
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">📅 التاريخ</TableHead>
                <TableHead className="text-right">💰 الإيرادات</TableHead>
                <TableHead className="text-right">📉 المصروفات</TableHead>
                <TableHead className="text-right">🏦 رصيد الصندوق</TableHead>
                <TableHead className="text-right">📊 الحالة</TableHead>
                <TableHead className="text-right">👤 المنشئ</TableHead>
                {canApproveReports && <TableHead className="text-right">⚙️ الإجراءات</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canApproveReports ? 7 : 6} className="text-center py-8">
                    <div className="flex flex-col items-center">
                      <BarChart3 className="h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium text-gray-900">لا توجد تقارير</p>
                      <p className="text-gray-600">ابدأ بإنشاء تقرير يومي جديد</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow key={report.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium text-right">
                      {formatDateWithEnglishNumerals(report.date)}
                    </TableCell>
                    <TableCell className="text-right text-green-600 font-semibold">
                      {report.income.toLocaleString()} دج
                    </TableCell>
                    <TableCell className="text-right text-red-600 font-semibold">
                      {report.expenses.toLocaleString()} دج
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {report.cashbox_balance.toLocaleString()} دج
                    </TableCell>
                    <TableCell className="text-right">
                      {getStatusBadge(report.status)}
                    </TableCell>
                    <TableCell className="text-right text-sm text-gray-600">
                      {report.created_by || 'غير محدد'}
                    </TableCell>
                    {canApproveReports && (
                      <TableCell>
                        <div className="flex justify-end space-x-2">
                          {report.status === 'pending' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApprove(report.id)}
                                className="text-green-600 hover:text-green-700 border-green-600"
                              >
                                ✅ موافقة
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReject(report.id)}
                                className="text-red-600 hover:text-red-700 border-red-600"
                              >
                                ❌ رفض
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyReportsManagement;
